<template>
  <div>
    <!-- Medicare Details Section -->
    <div class="mt-6">
      <h4 class="text-lg font-semibold text-primary mb-3">Medicare Details</h4>
      <p class="text-sm text-text-secondary mb-4">The information found on a patient's Medicare card, used to verify eligibility for health services</p>
      
      <vs-input
        :success="!errors.first('medicareNumber')"
        :danger="errors.first('medicareNumber') ? true : false"
        :danger-text="errors.first('medicareNumber')"
        val-icon-success="done"
        val-icon-danger="error"
        name="medicareNumber"
        data-vv-as="medicare card number"
        label="Medicare Card Number"
        placeholder="0000 00000 0"
        maxlength="12"
        class="w-full mb-3 vs-input-label-primary font-semibold vs-input-text-primary"
        v-model="medicareNumberFormatted" />

      <vs-input
        :success="!errors.first('medicareRefNumber')"
        :danger="errors.first('medicareRefNumber') ? true : false"
        :danger-text="errors.first('medicareRefNumber')"
        val-icon-success="done"
        val-icon-danger="error"
        name="medicareRefNumber"
        data-vv-as="individual reference number"
        label="Individual Reference Number (IRN)"
        placeholder="0"
        maxlength="1"
        class="w-full mb-3 vs-input-label-primary font-semibold vs-input-text-primary"
        v-model="medicareRefNumberFormatted" />

      <div :class="{'input-icon-validate-success': !errors.first('medicareExpiryDate'), 'input-icon-validate-danger': errors.first('medicareExpiryDate')}">
        <label class="vs-input--label text-primary">Valid to</label>
        <Datepicker 
          format="MM/yyyy" 
          :minimumView="'month'" 
          :maximumView="'year'"
          :initialView="'year'" 
          v-model="medicareExpiryModel" 
          :input-class="{
            'is-true vs-input--input': !errors.has('medicareExpiryDate'),
            'input-icon-validate-danger vs-input--input': errors.has('medicareExpiryDate'),
            'vs-input-label-primary': !errors.has('medicareExpiryDate'),
            'vs-input-text-primary': !errors.has('medicareExpiryDate')
          }"
          placeholder="MM/YYYY">
          <template slot="afterDateInput">
            <span v-if="!errors.has('medicareExpiryDate')" class="bg-color-error input-icon-validate vs-input--icon-validate">
              <i valiconpack="material-icons" class="vs-icon notranslate icon-scale material-icons null">done</i>
            </span>
            <span v-if="errors.has('medicareExpiryDate')" class="bg-color-error input-icon-validate vs-input--icon-validate">
              <i valiconpack="material-icons" class="vs-icon notranslate icon-scale material-icons null">error</i>
            </span>
          </template>
        </Datepicker>
        <small class="text-danger" style="font-size: 0.75em" v-if="errors.has('medicareExpiryDate')">
          {{ errors.first('medicareExpiryDate') }}
        </small>
      </div>
    </div>

    <!-- Divider with OR -->
    <div class="flex items-center my-6">
      <hr class="flex-1 border-t border-text-secondary" />
      <span class="px-4 text-sm font-medium text-text-secondary bg-white">OR</span>
      <hr class="flex-1 border-t border-text-secondary" />
    </div>

    <!-- IHI Section -->
    <div>
      <h4 class="text-lg font-semibold text-primary mb-3">Individual Healthcare Number (IHI)</h4>
      <p class="text-sm text-text-secondary mb-4">This is a unique 16-digit number used in Australia to identify individuals for healthcare purposes</p>
      
      <vs-input
        :success="!errors.first('ihiNumber')"
        :danger="errors.first('ihiNumber') ? true : false"
        :danger-text="errors.first('ihiNumber')"
        val-icon-success="done"
        val-icon-danger="error"
        name="ihiNumber"
        data-vv-as="IHI number"
        label="IHI Number"
        placeholder="0000 0000 0000 0000"
        maxlength="19"
        class="w-full vs-input-label-primary font-semibold vs-input-text-primary"
        v-model="ihiNumberFormatted" />
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import { formatMedicareNumber, formatIHINumber, removeNonDigits, parseMedicareExpiryDate, formatMedicareExpiryDate } from '@/utils/healthTreatment';

export default {
  name: 'PatientHealthIdentifiers',
  components: { Datepicker },
  props: {
    patientDetails: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  computed: {
    localPatientDetails: {
      get() {
        return this.patientDetails;
      },
      set(value) {
        this.$emit('update:patientDetails', value);
      },
    },
    medicareExpiryModel: {
      get() {
        return parseMedicareExpiryDate(this.patientDetails.medicareExpiryDate);
      },
              set(val) {
          if (val) {
            this.localPatientDetails.medicareExpiryDate = formatMedicareExpiryDate(val);
          } else {
            this.localPatientDetails.medicareExpiryDate = '';
          }
          this.errors.remove('medicareExpiryDate');
        },
    },
    medicareNumberFormatted: {
      get() {
        return formatMedicareNumber(this.patientDetails.medicareNumber);
      },
      set(val) {
        this.localPatientDetails.medicareNumber = removeNonDigits(val);
      },
    },
    medicareRefNumberFormatted: {
      get() {
        return this.patientDetails.medicareRefNumber;
      },
      set(val) {
        this.localPatientDetails.medicareRefNumber = removeNonDigits(val);
      },
    },
    ihiNumberFormatted: {
      get() {
        return formatIHINumber(this.patientDetails.ihiNumber);
      },
      set(val) {
        this.localPatientDetails.ihiNumber = removeNonDigits(val);
      },
    },
  },
};
</script>
